import React, { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { BootstrapApi } from 'vrlcm-gui-api';
import { AppSpec, AsyncOpState, BootStatus } from 'vrlcm-gui-types';
import {
  forbiddenResponseRedirectEffect,
  promisePoll,
  redirectToLoginPage
} from 'vrlcm-gui-utils';
import BootLoader from '../boot-loader/BootLoader';
import { l10n } from './i18n/i18nUtil';
import { AppHost } from './app-host';
import AppLevelErrorBoundary from './app-level-error-boundary/AppLevelErrorBoundary';
import { AppLoadingSpinner } from './app-loading-spinner';
import { espInit } from './esp/esp';
import { Login } from './login';
import './withAppRoot.css';

interface Props {
  component: React.ComponentType;
  app: AppSpec;
}

const AppRoot: React.FC<Props> = props => {
  const renderRoot = () => <AppHost {...props} />;

  useEffect(() => {
    espInit();
  }, []);

  useEffect(() => {
    /* Attach listener on request responses. */
    const { activate, deactivate } = forbiddenResponseRedirectEffect(() =>
      redirectToLoginPage(true)
    );
    activate();
    return deactivate;
  }, []);

  return (
    <AppLevelErrorBoundary>
      <Switch>
        <Route path="/login" component={Login} />
        <Route path="/" render={renderRoot} />
      </Switch>
    </AppLevelErrorBoundary>
  );
};

const AppBootStatusCheck: React.FC<Props> = props => {
  const { app } = props;
  const [fetchBootStatusState, setFetchBootStatusState] = useState<{
    state: AsyncOpState;
    bootStatus?: BootStatus;
  }>({
    state: AsyncOpState.Inprogress
  });

  useEffect(() => {
    setFetchBootStatusState({
      state: AsyncOpState.Inprogress
    });
    promisePoll(
      BootstrapApi.getBootStatus,
      [],
      (bootStatus: BootStatus) => {
        setFetchBootStatusState(previousState => {
          if (
            previousState.state === AsyncOpState.Completed &&
            previousState.bootStatus !== 'SUCCESS' &&
            bootStatus === 'SUCCESS'
          ) {
            /*
              Boot status changed from failed to success, this is the case where UI jar might
              be updated or LCM Upgrade or LCM Patched hence force reload to get latest UI.
            */
            window.location.reload(true);
          }

          return {
            ...previousState,
            state: AsyncOpState.Completed,
            bootStatus
          };
        });
        return bootStatus === 'SUCCESS';
      },
      5000
    ).then((bootStatus: BootStatus) => {
      setFetchBootStatusState(previousState => ({
        ...previousState,
        state: AsyncOpState.Completed,
        bootStatus
      }));
    });
  }, []);

  return (
    <>
      {fetchBootStatusState.state === AsyncOpState.Completed && (
        <>
          {fetchBootStatusState.bootStatus === 'SUCCESS' ? (
            <AppRoot {...props} />
          ) : (
            <BootLoader status={fetchBootStatusState.bootStatus} />
          )}
        </>
      )}
      {fetchBootStatusState.state === AsyncOpState.Inprogress && (
        <AppLoadingSpinner
          loadingText={l10n('components.withAppRoot.loadingApp', [app.name])}
        />
      )}
    </>
  );
};

const withAppRoot = (component: React.ComponentType, app: AppSpec) => {
  return () => <AppBootStatusCheck component={component} app={app} />;
};

export default withAppRoot;
